/**
 * @file: FloatingChatStack.tsx
 *
 * The floating chat panel's own navigation stack: a standalone (independent)
 * NavigationContainer hosting the SAME chat screens the inbox uses
 * (conversation / room detail / contact picker). Navigating between them — the
 * ⋮ → detail, detail's `+` → add-members picker, picker's new-group → a fresh
 * conversation — works directly, with the library's built-in transitions, back
 * button, and header chrome. No hand-rolled `showDetail` flags or slide
 * animations, and a new page is just another `Stack.Screen`.
 *
 * It's a SEPARATE container (the floating host sits beside the app's root
 * navigator, and a container hosts exactly one navigator), so cross-app pushes
 * can't bubble out of it — a task / service-record / report card and the
 * "open order" link go through the app-root ref (`getGlobalNavigationRef`); see
 * `useOpenChatCard` / `OrderLinkButton`.
 *
 * Providers: `ChatTransport` (the refcounted WS hold, so the connection is up
 * while the panel is open even over a non-chat screen) + `ChatContactsProvider`
 * (the add-members picker reads the contact store). NOT `ChatThreadsProvider` —
 * the panel has no thread list, and a second thread reducer would double-count
 * the shared unread badge; the conversation reads threads optionally.
 */

import { ChatScreens } from '@euler/app/flows/chat/screens';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { spreadScreens } from '@euler/functions/spreadScreens';
import { NavigationContainer, useNavigation } from '@react-navigation/native';
import {
  StackNavigationOptions,
  TransitionPresets,
  createStackNavigator,
} from '@react-navigation/stack';
import { ChevronLeft, X } from 'lucide-react-native';
import { FC, memo, useMemo } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { ChatContactsProvider } from '../contacts/provider';
import { ConversationIntent } from '../conversation/types';
import { ChatTransport } from '../transport';
import { FloatingPanelProvider } from './panelContext';

const Stack = createStackNavigator();

export const FloatingChatStack: FC<{
  intent: ConversationIntent;
  onClose: () => void;
}> = memo(({ intent, onClose }) => {
  const { rpx } = useResponsiveContext();
  // The panel sits BELOW the status bar, so the header / picker must not add the
  // top safe-area inset (it would leave the gap above the panel header). Zero
  // the top inset for everything inside the stack; keep bottom for the composer.
  const insets = useSafeAreaInsets();
  const panelInsets = useMemo(() => ({ ...insets, top: 0 }), [insets]);

  const screenOptions = useMemo<StackNavigationOptions>(
    () => ({
      ...TransitionPresets.SlideFromRightIOS,
      headerTitleAlign: 'center',
      // The panel is below the status bar (top inset zeroed above), so pin the
      // status-bar height to 0 and give the bar the app's standard rpx(56)
      // height (matches PaneHeader / the picker header) — otherwise it collapses
      // to the bare content height.
      headerStatusBarHeight: 0,
      headerStyle: {
        height: rpx(56),
        backgroundColor: '#fff',
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: '#f2f2f2',
        elevation: 0,
        shadowOpacity: 0,
      },
      headerTintColor: '#111',
      headerTitleStyle: {
        fontSize: rpx(18),
        fontFamily: FontFamily.NotoSans.Bold,
        color: '#111',
      },
      cardStyle: { flex: 1, backgroundColor: '#f7f7f7' },
      // Root screen (conversation) closes the panel; pushed screens go back.
      headerLeft: ({ canGoBack }) => (
        <HeaderLeft canGoBack={!!canGoBack} onClose={onClose} />
      ),
    }),
    [rpx, onClose],
  );

  return (
    <SafeAreaInsetsContext.Provider value={panelInsets}>
      <ChatTransport>
        <ChatContactsProvider>
          <NavigationContainer independent>
            <FloatingPanelProvider close={onClose}>
              <Stack.Navigator
                initialRouteName="_chatConversation"
                screenOptions={screenOptions}
              >
                {spreadScreens(
                  Stack.Screen,
                  ChatScreens,
                  '_chatConversation',
                  intent,
                )}
              </Stack.Navigator>
            </FloatingPanelProvider>
          </NavigationContainer>
        </ChatContactsProvider>
      </ChatTransport>
    </SafeAreaInsetsContext.Provider>
  );
});

const HeaderLeft: FC<{ canGoBack: boolean; onClose: () => void }> = memo(
  ({ canGoBack, onClose }) => {
    const { rpx } = useResponsiveContext();
    const navigation = useNavigation();
    return (
      <TouchableOpacity
        onPress={canGoBack ? () => navigation.goBack() : onClose}
        hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
        style={{ paddingHorizontal: rpx(12) }}
      >
        {canGoBack ? (
          <ChevronLeft size={rpx(24)} color="#111" />
        ) : (
          <X size={rpx(22)} color="#111" />
        )}
      </TouchableOpacity>
    );
  },
);
